/**
 * Copyright IBM Corp. 2022, 2026
 */

import { useEffect, useRef, useState } from 'react'

/**
 * Custom React hook that determines the active section on a page
 * based on the visibility of headings within the viewport. This hook
 * uses the Intersection Observer API to track which headings
 * (identified by their IDs/slugs) are visible as the user scrolls.
 * It optimistically updates the active section when scrolling upward and
 * no headings are visible.
 *
 * @param {string[]} slugs - An array of string slugs corresponding to the IDs
 * of headings on the page.
 *
 * @returns {string} The ID/slug of the currently active section. If no section
 * is active, it returns `undefined`.
 *
 */

export function useActiveSection(slugs: string[]): string {
	const visibleHeadings = useRef<Set<string>>(new Set())
	const [activeSection, setActiveSection] = useState<string>()
	const previousY = useRef<number>()

	useEffect(() => {
		visibleHeadings.current = new Set()

		const findMatchingSlug = (id: string) => {
			return slugs.find((s) => s === id)
		}

		const findMatchingIndex = (headingId: string): number => {
			const matchedSlug = findMatchingSlug(headingId)
			return slugs.findIndex((s) => s === matchedSlug)
		}

		const observer = new IntersectionObserver((entries) => {
			let currentY: number
			let scrollTrend: 'down' | 'up'

			entries.forEach((entry) => {
				currentY = window.scrollY
				scrollTrend = previousY.current < currentY ? 'down' : 'up'

				const targetEl = entry.target.querySelector('.__target-h')
				const entryId = targetEl.getAttribute('id')
				if (entry.isIntersecting) {
					visibleHeadings.current.add(entryId)
				} else {
					visibleHeadings.current.delete(entryId)
				}
			})

			const isSingleEntryLeaving =
				entries.length === 1 && !entries[0].isIntersecting
			const singleEntryLeavingIndex = isSingleEntryLeaving
				? findMatchingIndex(entries[0].target.id)
				: -1

			if (visibleHeadings.current.size > 0) {
				// Find the heading closest to the top of the viewport
				let shortestDistance = null
				let closestHeading = null
				visibleHeadings.current.forEach((headingId) => {
					const targetElement = document.getElementById(headingId)

					if (targetElement == null) {
						return
					}

					const distance = targetElement.getBoundingClientRect().bottom
					if (!closestHeading || distance < shortestDistance) {
						closestHeading = headingId
						shortestDistance = distance
					}
				})

				if (!closestHeading) {
					return
				}

				const activeSectionSlug = findMatchingSlug(closestHeading)
				setActiveSection(activeSectionSlug)
			} else if (previousY.current && scrollTrend === 'up') {
				// If we detect that we're scrolling up, and there are no visible
				// headers, optimistically set the previous header as visible to make
				// the active section match the visible content
				setActiveSection((current) => {
					const curActiveIndex = findMatchingIndex(current)

					// Handle an edge case where we get an intersection event for a
					// heading further down the page leaving intersection, otherwise
					// this would cause the active heading to incorrectly get bumped
					// up
					if (
						isSingleEntryLeaving &&
						singleEntryLeavingIndex > curActiveIndex
					) {
						return current
					}

					const newIndex = curActiveIndex - 1

					if (newIndex < 0) {
						return current
					}

					return slugs[newIndex]
				})
			}

			if (currentY) {
				previousY.current = currentY
			}
		})

		slugs.forEach((slug) => {
			const el = document.getElementById(slug)
			const heading = el.parentElement
			if (heading) {
				observer.observe(heading)
			}
		})

		return () => {
			observer.disconnect()
		}
	}, [slugs])

	return activeSection
}
